import React, { useState, useEffect } from "react";
import { staffAPI } from "../../services/api";
import "./POSCustomerLookup.css";

const POSCustomerLookup = ({ onCustomerSelect, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
  });

  // Search for customers when search term changes
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchCustomers = async () => {
      setLoading(true);
      try {
        const response = await staffAPI.searchCustomers({ search: searchTerm });
        setSearchResults(response.data.customers || []);
        setError(null);
      } catch (err) {
        console.error("Error searching customers:", err);
        setError("Failed to search customers. Please try again.");
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Handle customer selection
  const handleSelectCustomer = (customer) => {
    // Check if customer has a Stripe customer ID
    if (!customer.stripeCustomerId) {
      // Show confirmation dialog for customers without Stripe ID
      const proceed = window.confirm(
        `${customer.firstName} ${customer.lastName} does not have a Stripe account set up. ` +
          `You can either:\n\n` +
          `• Click "OK" to process payment without customer association\n` +
          `• Click "Cancel" to contact support to set up their Stripe account\n\n` +
          `Note: Processing without customer association means they won't receive membership benefits or session credits.`
      );

      if (!proceed) {
        return; // User chose to cancel
      }

      // Process without customer association
      onCustomerSelect({
        customerId: null, // No Stripe customer ID
        customerEmail: customer.email,
        customerName: `${customer.firstName} ${customer.lastName}`,
        customerPhone: customer.contactNumber,
        customerData: customer,
        mongoId: customer._id,
        noStripeAccount: true, // Flag to indicate this customer has no Stripe account
      });
    } else {
      // Normal flow for customers with Stripe customer ID
      onCustomerSelect({
        customerId: customer.stripeCustomerId, // Use Stripe customer ID instead of MongoDB _id
        customerEmail: customer.email,
        customerName: `${customer.firstName} ${customer.lastName}`,
        customerPhone: customer.contactNumber,
        customerData: customer,
        mongoId: customer._id, // Keep MongoDB ID for reference if needed
      });
    }
    onClose();
  };

  // Handle new customer form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({ ...prev, [name]: value }));
  };

  // Create new customer
  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await staffAPI.createMember(newCustomer);
      const customer = response.data.customer;

      handleSelectCustomer(customer);
    } catch (err) {
      console.error("Error creating customer:", err);
      setError("Failed to create customer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pos-customer-lookup-overlay">
      <div className="pos-customer-lookup-modal">
        <div className="pos-customer-lookup-header">
          <h2>Customer Lookup</h2>
          <button className="pos-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {error && <div className="pos-error">{error}</div>}

        {!showNewCustomerForm ? (
          <>
            <div className="pos-search-container">
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pos-search-input"
              />
            </div>

            <div className="pos-search-results">
              {loading ? (
                <div className="pos-loading">Searching...</div>
              ) : searchResults.length > 0 ? (
                <ul className="pos-customer-list">
                  {searchResults.map((customer) => (
                    <li
                      key={customer._id}
                      className="pos-customer-item"
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <div className="pos-customer-name">
                        {customer.firstName} {customer.lastName}
                        {!customer.stripeCustomerId && (
                          <span
                            className="pos-no-stripe-badge"
                            title="No Stripe account - payment will be processed without customer association"
                          >
                            ⚠️ No Stripe Account
                          </span>
                        )}
                      </div>
                      <div className="pos-customer-details">
                        <span>{customer.email}</span>
                        {customer.contactNumber && (
                          <span>{customer.contactNumber}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : searchTerm.length >= 2 ? (
                <div className="pos-no-results">
                  <p>No customers found matching "{searchTerm}"</p>
                </div>
              ) : (
                <div className="pos-search-prompt">
                  <p>Enter at least 2 characters to search</p>
                </div>
              )}
            </div>

            <div className="pos-customer-actions">
              <button
                className="pos-new-customer-btn"
                onClick={() => setShowNewCustomerForm(true)}
              >
                Create New Customer
              </button>
            </div>
          </>
        ) : (
          <form
            onSubmit={handleCreateCustomer}
            className="pos-new-customer-form"
          >
            <div className="pos-form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={newCustomer.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="pos-form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={newCustomer.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="pos-form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={newCustomer.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="pos-form-group">
              <label htmlFor="contactNumber">Phone Number</label>
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={newCustomer.contactNumber}
                onChange={handleInputChange}
              />
            </div>
            <div className="pos-form-actions">
              <button
                type="button"
                className="pos-cancel-btn"
                onClick={() => setShowNewCustomerForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="pos-create-btn"
                disabled={loading}
              >
                {loading ? "Creating..." : "Create Customer"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default POSCustomerLookup;
